import { PROBLEM_CONTENT_TYPE, problemDocumentSchema, } from '@platform/schema';
import { client } from './generated/client.gen';
/**
 * The only hand-written file in this package's source, and deliberately outside
 * `src/generated` so codegen never overwrites it.
 *
 * It does four things the generated code cannot:
 *   1. resolves the base URL from the caller's environment
 *   2. injects the auth token, through a getter so this package never learns which
 *      auth vendor is in use
 *   3. **generates the correlation id** — it originates here, on the client, which
 *      is what makes a trace start at the tap rather than at the server
 *   4. turns an RFC 9457 problem document into a typed `ApiError`
 */
export const CORRELATION_ID_HEADER = 'x-correlation-id';
/**
 * A failed request, carrying everything needed to act on it: a stable `code` to
 * branch on, the `correlationId` to quote, per-field `details`, and whether a retry
 * could plausibly succeed.
 */
export class ApiError extends Error {
    code;
    status;
    correlationId;
    details;
    retryable;
    constructor(init) {
        super(init.message);
        this.name = 'ApiError';
        this.code = init.code;
        this.status = init.status;
        this.correlationId = init.correlationId;
        this.details = init.details ?? [];
        this.retryable = init.retryable;
    }
}
/**
 * No response at all: aeroplane mode, a dead server, DNS. Distinct from `ApiError`
 * because the user-facing answer is different — "you're offline" rather than
 * "something went wrong" — and because it is always worth retrying.
 */
export class OfflineError extends Error {
    correlationId;
    constructor(correlationId) {
        super('The request could not reach the server.');
        this.name = 'OfflineError';
        this.correlationId = correlationId;
    }
}
/**
 * The id most recently put on an outbound request.
 *
 * A transport failure produces no response, so there is nothing to read the id
 * back from — and reporting a freshly generated one would hand the user an id
 * that appears in no log anywhere. This is best-effort by construction: with
 * concurrent in-flight requests it names the latest, not necessarily the one that
 * failed. Better than an id that is certainly wrong.
 */
let lastCorrelationId = 'unset';
/**
 * Read by the error reporter, which needs the id at the moment an event is sent
 * rather than at the moment a request is made — the two are rarely the same call
 * stack. `'unset'` until the first request, which is honest: an error thrown before
 * anything was fetched belongs to no trace.
 */
export function getLastCorrelationId() {
    return lastCorrelationId;
}
export function newCorrelationId() {
    // crypto.randomUUID exists in Node 19+, modern browsers, and Hermes with the
    // polyfill Expo ships. The DOM lib asserts it is always present, which is untrue
    // on older Hermes — hence the deliberately widened type and the runtime check.
    // The fallback keeps this package free of a uuid dependency.
    const host = globalThis;
    if (typeof host.crypto?.randomUUID === 'function') {
        return host.crypto.randomUUID();
    }
    return `cid-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
/**
 * Wire the generated client up. Called once, at application start, by whichever app
 * is doing the calling.
 */
let registered = null;
export function configureClient({ baseUrl, getToken }) {
    client.setConfig({ baseUrl });
    // Ejecting first makes this idempotent. Interceptors accumulate, so calling
    // configure twice — a hot reload, a sign-in that reconfigures the token getter —
    // would otherwise leave the previous getter still injecting the previous token.
    if (registered) {
        client.interceptors.request.eject(registered.request);
        client.interceptors.response.eject(registered.response);
        registered = null;
    }
    const onRequest = async (request) => {
        lastCorrelationId = newCorrelationId();
        request.headers.set(CORRELATION_ID_HEADER, lastCorrelationId);
        if (getToken) {
            const token = await getToken();
            if (token !== null && token !== '') {
                request.headers.set('Authorization', `Bearer ${token}`);
            }
        }
        return request;
    };
    const onResponse = async (response, request) => {
        if (response.ok)
            return response;
        const correlationId = response.headers.get(CORRELATION_ID_HEADER) ??
            request.headers.get(CORRELATION_ID_HEADER) ??
            'unset';
        // Only a problem document is trustworthy here. Anything else — an HTML error
        // page from a proxy, an empty body — becomes a generic error rather than being
        // parsed hopefully.
        if (response.headers.get('content-type')?.includes(PROBLEM_CONTENT_TYPE)) {
            const parsed = problemDocumentSchema.safeParse(await response.clone().json());
            if (parsed.success) {
                const problem = parsed.data;
                throw new ApiError({
                    code: problem.type,
                    status: problem.status,
                    message: problem.detail,
                    correlationId: problem.instance,
                    details: problem.details,
                    retryable: problem.retryable,
                });
            }
        }
        throw new ApiError({
            code: 'RESPONSE_UNRECOGNISED',
            status: response.status,
            message: `The server returned ${String(response.status)} without a problem document.`,
            correlationId,
            retryable: response.status >= 500,
        });
    };
    client.interceptors.request.use(onRequest);
    client.interceptors.response.use(onResponse);
    registered = { request: onRequest, response: onResponse };
}
/** Wraps a transport failure so callers can tell "offline" from "rejected". */
export async function withOfflineDetection(call) {
    try {
        return await call();
    }
    catch (error) {
        if (error instanceof ApiError)
            throw error;
        if (error instanceof TypeError)
            throw new OfflineError(lastCorrelationId);
        throw error;
    }
}
//# sourceMappingURL=runtime.js.map